import {
  type BanUserRequest,
  type ChangeUserStatusRequest,
  type RestoreUserRequest,
  type SuspendUserRequest,
  type UserDetail,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/**
 * The account lifecycle writes — **one door for two modules** (P27c1).
 *
 * The staff page (`Admins`) and the members page (`Members`) both suspend,
 * restore, ban and restatus an account through the same four routes and the
 * same three dialogs. A module may not import another's api (W6), and a dialog
 * in the kit may not import a module's (W2), so the writes live here beside the
 * dialogs that make them.
 *
 * It caches exactly one thing: the account the status dialog is about to move,
 * because the moves on offer depend on where the account is *now* and a status
 * carried in `ModalData` could be stale by the time the dialog opens. The lists
 * that must refresh belong to the modules. Each write publishes
 * `members.status.changed`; the module apis subscribe (`registerApi`'s
 * `subscribe` slot) and invalidate their own tags, which is the only way a
 * write in one slice reaches a list in another (W5).
 *
 * The four endpoints are **separate on purpose**, mirroring N9 on the server:
 * `suspend` takes a mandatory end date, `ban` a mandatory reason the member is
 * actually shown, and neither is reachable through the generic status route. A
 * client that collapsed them into one call would be asking for the indefinite
 * suspension the api is built to make impossible.
 */
export const accountStatusApi = createApi({
  reducerPath: 'accountStatusApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['AccountLifecycle'],
  endpoints: (builder) => ({
    /** Where the account is now — what the status dialog offers moves FROM. */
    accountLifecycle: builder.query<UserDetail, string>({
      query: (id) => ({ url: `/admin/users/${id}`, method: 'GET' }),
      providesTags: (_r, _e, id) => [{ type: 'AccountLifecycle' as const, id }],
    }),

    suspendAccount: builder.mutation<UserDetail, { id: string; body: SuspendUserRequest }>({
      query: ({ id, body }) => ({ url: `/admin/users/${id}/suspend`, method: 'POST', body }),
      invalidatesTags: lifecycleOf,
      onQueryStarted: announceStatusChange,
    }),

    restoreAccount: builder.mutation<UserDetail, { id: string; body: RestoreUserRequest }>({
      query: ({ id, body }) => ({ url: `/admin/users/${id}/restore`, method: 'POST', body }),
      invalidatesTags: lifecycleOf,
      onQueryStarted: announceStatusChange,
    }),

    banAccount: builder.mutation<UserDetail, { id: string; body: BanUserRequest }>({
      query: ({ id, body }) => ({ url: `/admin/users/${id}/ban`, method: 'POST', body }),
      invalidatesTags: lifecycleOf,
      onQueryStarted: announceStatusChange,
    }),

    /**
     * The generic lifecycle route (N9). Separate from suspend/ban above because
     * it carries no end date and no member-facing reason — it is how staff move
     * an account between the states the machine allows, not how they enforce.
     */
    changeAccountStatus: builder.mutation<
      UserDetail,
      { id: string; body: ChangeUserStatusRequest }
    >({
      query: ({ id, body }) => ({ url: `/admin/users/${id}/status`, method: 'POST', body }),
      invalidatesTags: lifecycleOf,
      onQueryStarted: announceStatusChange,
    }),
  }),
});

function lifecycleOf(
  _result: unknown,
  _error: unknown,
  { id }: { id: string },
): { type: 'AccountLifecycle'; id: string }[] {
  return [{ type: 'AccountLifecycle', id }];
}

/**
 * Announces a landed write and nothing else. The refusal is swallowed
 * deliberately: `onQueryStarted` has no caller, so a rejection escaping it
 * becomes an unhandled rejection; the failure is already the caller's, through
 * `unwrap()`.
 */
async function announceStatusChange(
  { id }: { id: string },
  { queryFulfilled }: { queryFulfilled: Promise<unknown> },
): Promise<void> {
  try {
    await queryFulfilled;
    EVENT_BUS.publish({ type: 'members.status.changed', userId: id });
  } catch {
    // Refused — nothing changed, so there is nothing to announce.
  }
}

export const {
  useAccountLifecycleQuery,
  useSuspendAccountMutation,
  useRestoreAccountMutation,
  useBanAccountMutation,
  useChangeAccountStatusMutation,
} = accountStatusApi;
